import React, { useRef, useState, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "./button";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

interface AvatarUploaderProps {
  currentPhotoUrl?: string | null;
  fullName: string;
  onSuccess?: (newPhotoUrl: string) => void;
}

export function AvatarUploader({
  currentPhotoUrl,
  fullName,
  onSuccess,
}: AvatarUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    currentPhotoUrl || null
  );
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    setPreviewUrl(currentPhotoUrl || null);
  }, [currentPhotoUrl]);

  const utils = trpc.useUtils();

  const getUploadUrlMutation = trpc.media.getUploadUrl.useMutation();
  const confirmUploadMutation = trpc.media.confirmUpload.useMutation();
  const deleteMediaMutation = trpc.media.deleteMedia.useMutation();

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Photo size must be less than 5MB");
      return;
    }

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file (JPEG, PNG, WebP)");
      return;
    }

    // Instant optimistic preview
    const reader = new FileReader();
    reader.onload = () => setPreviewUrl(reader.result as string);
    reader.readAsDataURL(file);

    try {
      // 1. Get presigned URL
      const { url, objectKey } = await getUploadUrlMutation.mutateAsync({
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        category: "avatar",
      });

      // 2. Upload directly to R2
      const uploadRes = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });

      if (!uploadRes.ok) {
        throw new Error("Failed to upload to Cloudflare R2");
      }

      // 3. Confirm upload
      const confirmRes = await confirmUploadMutation.mutateAsync({
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        category: "avatar",
        objectKey,
      });

      toast.success("Profile photo updated successfully");
      
      // Update global profile draft with the new URL
      const finalUrl = confirmRes.media.r2Url;
      setPreviewUrl(finalUrl);
      if (onSuccess) onSuccess(finalUrl);
      utils.profile.getMyProfile.invalidate();

    } catch (err: any) {
      toast.error(err.message || "Failed to upload profile photo");
      setPreviewUrl(currentPhotoUrl || null); // revert preview on error
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemovePhoto = async () => {
    if (!previewUrl) return;
    const confirmed = window.confirm(
      "Are you sure you want to remove your profile photo? This action will permanently delete it from your cloud storage."
    );
    if (!confirmed) return;

    try {
      setIsDeleting(true);
      await deleteMediaMutation.mutateAsync({ url: previewUrl });
      setPreviewUrl(null);
      if (onSuccess) onSuccess("");
      utils.profile.getMyProfile.invalidate();
      toast.success("Profile photo removed successfully");
    } catch (err: any) {
      toast.error(err.message || "Failed to remove photo");
    } finally {
      setIsDeleting(false);
    }
  };

  const isUploading = getUploadUrlMutation.isPending || confirmUploadMutation.isPending;

  const initialLetter = fullName.trim().charAt(0).toUpperCase() || "P";

  return (
    <div className="flex items-center gap-4">
      <div
        className="relative group flex h-20 w-20 cursor-pointer items-center justify-center overflow-hidden rounded-full border-2 border-primary/40 bg-muted text-2xl font-bold font-serif text-primary shadow-sm hover:border-primary transition-all"
        onClick={() => fileInputRef.current?.click()}
        title="Click to upload profile photo"
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={fullName}
            className="h-full w-full object-cover"
          />
        ) : (
          <span>{initialLetter}</span>
        )}

        <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-white opacity-0 group-hover:opacity-100 transition-opacity text-xs font-sans font-medium">
          Change
        </div>

        {(isUploading || isDeleting) && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-white text-xs font-sans font-medium animate-pulse">
            {isDeleting ? "Deleting..." : "Uploading..."}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={handleFileSelected}
        />
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading || isDeleting}
          >
            {isUploading ? "Uploading..." : previewUrl ? "Change Photo" : "Upload Photo"}
          </Button>
          {previewUrl && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleRemovePhoto}
              disabled={isUploading || isDeleting}
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
              title="Remove profile photo and delete from cloud storage"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" />
              {isDeleting ? "Deleting..." : "Remove"}
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          Recommended square JPG, PNG, or WebP. Max 5MB.
        </p>
      </div>
    </div>
  );
}
